import React, { useState, useEffect, useRef } from 'react';
import { Fingerprint, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { OverlayQueue, OVERLAY_PRIORITY } from '../utils/overlayQueue';

const STORAGE_KEY = 'sv_biometric_pref';
const DISMISS_KEY = 'sv_biometric_dismiss_at';
const COOLDOWN_DAYS = 30;
const TOUR_SITE_KEY = 'sv_tour_site_done';
const TOUR_SYSTEM_KEY = 'sv_tour_system_done';
const TOUR_COMPLETED_EVENT = 'sv_tour_completed';
const BIOMETRIC_DELAY_MS = 3000; // 3 seconds after tour completion

function isMobileDevice() {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua) || (window.innerWidth <= 768 && 'ontouchstart' in window);
}

function supportsWebAuthn() {
    return typeof window !== 'undefined' && window.PublicKeyCredential !== undefined;
}

export default function BiometricPrompt() {
    const [show, setShow] = useState(false);
    const [animating, setAnimating] = useState(false);
    const timerRef = useRef(null);
    const acquiredRef = useRef(false);

    useEffect(() => {
        // Never show if not mobile, already enabled/never, or cooldown active
        if (!isMobileDevice()) return;
        const pref = localStorage.getItem(STORAGE_KEY);
        if (pref === 'enabled' || pref === 'never') return;
        const dismissAt = localStorage.getItem(DISMISS_KEY);
        if (dismissAt) {
            const diff = (Date.now() - parseInt(dismissAt)) / 86400000;
            if (diff < COOLDOWN_DAYS) return;
        }
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;

        let cancelled = false;

        // Acquire queue slot then show. Re-checks conditions after the slot is granted.
        const requestSlotAndShow = async () => {
            await OverlayQueue.acquire('biometric', OVERLAY_PRIORITY.BIOMETRIC);
            if (cancelled) { OverlayQueue.release('biometric'); return; }
            // Re-check preferences (user may have changed them while waiting)
            const p = localStorage.getItem(STORAGE_KEY);
            if (p === 'enabled' || p === 'never') { OverlayQueue.release('biometric'); return; }
            const d = localStorage.getItem(DISMISS_KEY);
            if (d && (Date.now() - parseInt(d)) / 86400000 < COOLDOWN_DAYS) { OverlayQueue.release('biometric'); return; }
            acquiredRef.current = true;
            setAnimating(true);
            setTimeout(() => setShow(true), 50);
        };

        // Check if tour was already completed in a PREVIOUS session
        const tourSiteDone = localStorage.getItem(TOUR_SITE_KEY);
        const tourSystemDone = localStorage.getItem(TOUR_SYSTEM_KEY);

        if (tourSiteDone || tourSystemDone) {
            const completedThisSession = sessionStorage.getItem('sv_tour_just_completed');
            if (!completedThisSession) {
                // Tour was completed in a previous session: delay, then go through the queue
                // (it will wait for any category / tab tour still open).
                timerRef.current = setTimeout(() => { requestSlotAndShow(); }, BIOMETRIC_DELAY_MS);
            }
            // If completedThisSession is true, the event listener below handles it.
        }

        // Listen for tour-completed event (fired when user clicks "Comecar agora")
        const handleTourCompleted = () => {
            sessionStorage.setItem('sv_tour_just_completed', 'true');
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => { requestSlotAndShow(); }, BIOMETRIC_DELAY_MS);
        };

        window.addEventListener(TOUR_COMPLETED_EVENT, handleTourCompleted);

        return () => {
            cancelled = true;
            window.removeEventListener(TOUR_COMPLETED_EVENT, handleTourCompleted);
            if (timerRef.current) clearTimeout(timerRef.current);
            // Release any pending/active slot to avoid stalling the queue on unmount.
            OverlayQueue.release('biometric');
            acquiredRef.current = false;
        };
    }, []);

    const handleEnable = async () => {
        try {
            if (supportsWebAuthn()) {
                localStorage.setItem(STORAGE_KEY, 'enabled');
                toast.success('Biometria ativada! Na proxima vez, voce podera entrar com sua digital.');
            } else {
                localStorage.setItem(STORAGE_KEY, 'enabled_pending');
                toast.success('Preferencia salva! A biometria sera ativada quando disponivel no seu dispositivo.');
            }
        } catch {}
        closePrompt(true);
    };

    const handleLater = () => {
        localStorage.setItem(DISMISS_KEY, Date.now().toString());
        closePrompt(false);
    };

    const handleNever = () => {
        localStorage.setItem(STORAGE_KEY, 'never');
        closePrompt(false);
    };

    const closePrompt = (confirmed = false) => {
        setShow(false);
        setTimeout(() => setAnimating(false), 300);
        // Notify that biometric decision was made, with confirmation status
        window.dispatchEvent(new CustomEvent('sv_biometric_resolved', { detail: { confirmed } }));
        // Release the queue (end of sequence)
        OverlayQueue.release('biometric');
        acquiredRef.current = false;
    };

    if (!animating) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4" style={{ pointerEvents: show ? 'auto' : 'none' }}>
            <div className="absolute inset-0 transition-opacity duration-300" style={{ background: 'rgba(0,0,0,0.5)', opacity: show ? 1 : 0 }} onClick={handleLater} />
            <div className="relative w-full max-w-md rounded-t-3xl p-6 transition-all duration-300"
                style={{
                    background: '#0B214A', border: '1px solid rgba(125,165,255,0.16)', borderBottom: 'none',
                    transform: show ? 'translateY(0)' : 'translateY(100%)', opacity: show ? 1 : 0,
                }}
                data-testid="biometric-prompt">
                <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: '#12397D' }} />
                <button onClick={handleLater} className="absolute top-4 right-4 p-1.5 rounded-full" style={{ background: '#0D2C63' }}>
                    <X size={14} style={{ color: '#8A8A7A' }} />
                </button>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.10)', border: '1px solid rgba(var(--sv-accent-rgb), 0.20)' }}>
                    <Fingerprint size={28} style={{ color: 'var(--sv-gold-primary)' }} />
                </div>
                <h3 className="text-center text-base font-bold mb-2" style={{ color: '#F5F5F0', fontFamily: "var(--font-display)" }}>
                    Entrar mais rapido?
                </h3>
                <p className="text-center text-xs mb-6" style={{ color: '#8A8A7A', lineHeight: 1.6 }}>
                    Ative a biometria para acessar o SuperVitrine com sua digital ou reconhecimento facial.
                </p>
                <div className="space-y-2.5">
                    <button onClick={handleEnable}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                        style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                        data-testid="biometric-enable">
                        <Fingerprint size={16} /> Ativar biometria
                    </button>
                    <button onClick={handleLater}
                        className="w-full py-3 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                        style={{ background: '#0D2C63', color: '#8A8A7A', border: '1px solid rgba(125,165,255,0.16)' }}
                        data-testid="biometric-later">
                        <Clock size={12} /> Agora nao
                    </button>
                    <button onClick={handleNever}
                        className="w-full py-2 text-[11px] font-medium"
                        style={{ color: '#555' }}
                        data-testid="biometric-never">
                        Nao mostrar novamente
                    </button>
                </div>
                <p className="text-center text-[10px] mt-4" style={{ color: '#444' }}>
                    Seus dados biometricos ficam apenas no seu dispositivo.
                </p>
            </div>
        </div>
    );
}
